import { API } from '../apis/limeAuthUtils/apiWrapper';
import { getAuthenticatedUser, getAuthenticatedUserEmail } from '../apis/limeAuthUtils/limeToken';
import copy from 'copy-to-clipboard';
import React, { useContext } from 'react';
import { Button, StatusIndicator, Box, Popover, SpaceBetween, Spinner } from '@amzn/awsui-components-console';
import '../../styles/custom.scss';
import { CONTACT_US } from './constants';
import { RegionContext } from '../CustomContext';

/**
 * Get current authenticated user alias
 * @returns {Promise<string>} User alias (e.g., "qianfeih")
 */
export async function getCurrentAuthUserAlias() {
    return await getAuthenticatedUser();
}

/**
 * Get current authenticated user email
 * @returns {Promise<string>} User email (e.g., "qianfeih@amazon.com")
 */
export async function getCurrentAuthUserEmail() {
    return await getAuthenticatedUserEmail();
}

/**
 * Check if the current user is a Midway (1P) user
 * @returns {boolean} true if user is from Midway, false otherwise
 */
export function isMidwayUser() {
    try {
        const identity = window.loom.getUserIdentity();
        const midwayIdentity = identity.identities.find((i) => i.providerName === 'MIDWAY');
        // TODO: get identity when third party, need follow up with Lime
        return !!midwayIdentity;
    } catch (e) {
        console.error('Failed to check if user is from Midway', e);
        return false;
    }
}

export async function isAdmin() {
    /**
     * function to check if user is admin
     */
    const authenticatedUserAlias = await getCurrentAuthUserAlias();
    const apiName = 'v2/permission-validate';
    const myInit = {
        body: {
            user: authenticatedUserAlias,
        },
    };
    const response = await API.post(apiName, '/v2/is_admin', myInit);
    return response;
}

export async function isLabAdmin() {
    /**
     * function to check if user is admin
     */
    const authenticatedUserAlias = await getCurrentAuthUserAlias();
    const apiName = 'v2/permission-validate';
    const myInit = {
        body: {
            user: authenticatedUserAlias,
        },
    };
    return await API.post(apiName, '/v2/is_lab_admin', myInit);
}

export function generateManualTestingUrl(token, region) {
    /**
     * helper function to generate manual testing url
     * token: jwt token
     */
    console.log(`Generating url for region ${region}`);
    const stage = getStageName();
    const url =
        stage === 'local'
            ? `https://ftdf-alpha-${region}-alb.devicefarm.ds2.amazon.dev/`
            : `https://ftdf-${stage}-${region}-alb.devicefarm.ds2.amazon.dev/`;
    return url.concat('?jwt=', token);
}

export function getStageName() {
    return process.env.NODE_ENV === 'production' ? 'alpha' : 'local';
}

export function copyToClipboard(text) {
    /**
     * helper function to create a customized copy to clipboard componet
     * text: the text you would like to copy
     * polaris doesn't provide such component and instead provide code exmaples: https://polaris.a2z.com/components/copy-to-clipboard/
     * utilize the example provided by polaris with copy-to-clipboard library https://www.npmjs.com/package/copy-to-clipboard
     */
    return (
        <span>
            <SpaceBetween direction="horizontal" size="xxs" className="custom-code">
                <Box variant="code">{text}</Box>
                <Box display="inline-block">
                    <Popover
                        size="small"
                        position="top"
                        triggerType="custom"
                        dismissButton={false}
                        content={<StatusIndicator type="success">copied</StatusIndicator>}
                    >
                        <Button variant="inline-icon" iconName="copy" onClick={() => copy(text)} />
                    </Popover>
                </Box>
            </SpaceBetween>
        </span>
    );
}

export async function getTicket(status) {
    /**
     * helper function to get sev2 ticket from ticket table
     * @param status
     * @returns
     */
    const apiName = 'v1/sev2-monitor';
    return await API.get(apiName, `/tickets/${status}`, {});
}

export function isValidInput(input) {
    /**
     * helper function to validate input string
     * check if input is not empty
     */
    return input !== null && input.trim().length > 0;
}

export function isValidEmailInput(input) {
    /**
     * helper function to validate input string
     * checks if input is not empty and contains '@'
     */
    return input !== null && input.trim().length > 0 && input.includes('@');
}

export function isAlphanumericInput(input) {
    /**
     * helper function to validate input string
     * checks if input only contains uppercase letters (A-Z) and numbers (0-9)
     */
    const alphanumericRegex = /^[A-Z0-9]+$/;
    return alphanumericRegex.test(input);
}

/**
 * display error message
 * @param {*} header
 * @param {*} content
 * @param {*} setErrorMessage
 */
export const displayErrorContent = (header, content, setErrorMessage) => {
    setErrorMessage([
        {
            header: header,
            type: 'error',
            dismissible: true,
            dismissLabel: 'Dismiss message',
            onDismiss: () => setErrorMessage([]),
            content: <>{content}</>,
            action: (
                <Button href={CONTACT_US} iconAlign="right" target="_blank">
                    Contact us
                </Button>
            ),
        },
    ]);
};

/**
 * display success message
 * @param {*} content
 */
export const displaySuccessContent = (content, setSuccessMessage) => {
    // display success message
    setSuccessMessage([
        {
            type: 'success',
            dismissible: true,
            dismissLabel: 'Dismiss message',
            onDismiss: () => setSuccessMessage([]),
            content: <>{content}</>,
        },
    ]);
};

/**
 * Reusable loading spinner component
 * @returns {JSX.Element} Centered loading spinner
 */
export const LoadingSpinner = () => {
    return (
        <Box textAlign="center" padding={{ top: 'xxxl' }}>
            <Spinner size="large" />
        </Box>
    );
};
